//src\components\portal\data_settings.js
import React, { useState, useEffect } from 'react';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  TextField,
  Button,
  Grid,
  Snackbar,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import DataManager from './data_manager/data_manager';
import portalSession from '../portal_services/portal_session'; 

const CoreConfig = () => {
  const theme = useTheme();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form state
  const [autopilot, setAutopilot] = useState(true);
  const [timeframeNumber, setTimeframeNumber] = useState(30);
  const [timeframeUnit, setTimeframeUnit] = useState('d');
  const [updateFrequency, setUpdateFrequency] = useState('weekly');

  // API configuration
  const BASE_URL = portalSession.getBaseURL();
  const CUSTOMER_ID = portalSession.getCustomerId();
  const CUSTOMER_TOKEN = portalSession.getCustomerToken();

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/get_customer/${CUSTOMER_ID}`, {
          method: 'GET',
          headers: {
            'X-Customer-ID': CUSTOMER_ID,
            'X-Customer-Token': CUSTOMER_TOKEN,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setCustomerData(data);
        
        // Parse timeframe (e.g., "30d" -> 30 and "d")
        const timeframe = data.company_crawl_timeframe || '30d';
        const match = timeframe.match(/^(\d+)([dw])$/);
        if (match) {
          setTimeframeNumber(parseInt(match[1]));
          setTimeframeUnit(match[2]);
        }
        
        setUpdateFrequency(data.company_update_frequency || 'weekly');
        setAutopilot(data.company_mode_auto ?? true);
        
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching customer data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        company_crawl_timeframe: `${timeframeNumber}${timeframeUnit}`,
        company_update_frequency: updateFrequency,
        company_mode_auto: autopilot
      };

      const response = await fetch(`${BASE_URL}/edit_customer/${CUSTOMER_ID}`, {
        method: 'POST',
        headers: {
          'X-Customer-ID': CUSTOMER_ID,
          'X-Customer-Token': CUSTOMER_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSuccessMessage('Data settings saved successfully!');
        // Update the customer data state
        setCustomerData(prev => ({ 
          ...prev, 
          company_crawl_timeframe: `${timeframeNumber}${timeframeUnit}`,
          company_update_frequency: updateFrequency,
          company_mode_auto: autopilot
        }));
      } else {
        throw new Error(result.message || 'Failed to save settings');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  // Validation
  const isTimeframeValid = timeframeNumber > 0 && timeframeNumber <= 365;
  const canSave = isTimeframeValid;

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress color="primary" />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            Error loading data settings: {error}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Card 
          elevation={3}
          sx={{ 
            backgroundColor: 'white',
            border: '2px solid #009688',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                color: '#009688',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 3
              }}
            >
              <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Data Settings
            </Typography>
            
            {/* Autopilot Toggle */}
            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autopilot}
                    onChange={(e) => setAutopilot(e.target.checked)}
                    color="primary"
                    size="large"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <AutoModeIcon sx={{ mr: 1, color: autopilot ? '#009688' : '#ccc' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Autopilot Mode
                    </Typography>
                  </Box>
                }
                sx={{ 
                  '& .MuiFormControlLabel-label': {
                    color: autopilot ? '#009688' : '#666'
                  }
                }}
              />
              
              {/* Autopilot Information Box */}
              <Paper 
                elevation={1} 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: autopilot ? '#e8f5e8' : '#fff3e0',
                  border: `1px solid ${autopilot ? '#4caf50' : '#ff9800'}`
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <InfoIcon sx={{ color: autopilot ? '#4caf50' : '#ff9800', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {autopilot ? 'Autopilot ON:' : 'Autopilot OFF:'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {autopilot 
                        ? "The system automatically executes data management actions based on your configured timeframe and frequency settings without requiring manual intervention."
                        : "The timeframe and frequency parameters define the scope of data that the data manager analyzes and presents to you, but all actions require manual approval/execution."
                      }
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      You can toggle autopilot at any time to switch between automated and manual control as needed.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              {/* Crawl Timeframe */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography 
                    variant="h6" 
                    component="h2"
                    sx={{ 
                      color: '#009688',
                      fontWeight: 'bold'
                    }}
                  >
                    Crawl Timeframe
                  </Typography>
                  <Chip 
                    label="Recommended: 52 weeks" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  How far back in time to look for data
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    type="number"
                    value={timeframeNumber}
                    onChange={(e) => setTimeframeNumber(parseInt(e.target.value) || 0)}
                    error={!isTimeframeValid}
                    helperText={!isTimeframeValid ? 'Must be between 1 and 365' : ''}
                    inputProps={{ min: 1, max: 365 }}
                    sx={{ 
                      width: '120px',
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#009688',
                        },
                      },
                    }}
                  />
                  <FormControl sx={{ width: '120px' }}>
                    <Select
                      value={timeframeUnit}
                      onChange={(e) => setTimeframeUnit(e.target.value)}
                      sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#009688',
                        },
                      }}
                    >
                      <MenuItem value="d">Days</MenuItem>
                      <MenuItem value="w">Weeks</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body1" sx={{ 
                    backgroundColor: '#f0f9ff',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    border: '1px solid #e0f2f1',
                    fontWeight: 'bold'
                  }}>
                    {timeframeNumber}{timeframeUnit}
                  </Typography>
                </Box>
                
                {/* Timeframe Information */}
                <Paper elevation={1} sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa' }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <TipsAndUpdatesIcon sx={{ color: '#009688', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Why 52 weeks is recommended:
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        A full year captures complete data patterns, seasonal variations, and content lifecycle trends. 
                        This timeframe can be customized based on your site size, update frequency, and specific business needs.
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Update Frequency */}
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    color: '#009688',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  Update Frequency
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  How often data will be updated
                </Typography>
                <FormControl fullWidth>
                  <InputLabel 
                    sx={{ 
                      '&.Mui-focused': { color: '#009688' }
                    }}
                  >
                    Frequency
                  </InputLabel>
                  <Select
                    value={updateFrequency}
                    label="Frequency"
                    onChange={(e) => setUpdateFrequency(e.target.value)}
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#009688',
                      },
                    }}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Current Settings Display */}
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    color: '#009688',
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  Current Settings
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f0f9ff',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #e0f2f1'
                }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Timeframe:</strong> {customerData?.company_crawl_timeframe}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Frequency:</strong> {customerData?.company_update_frequency}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Mode:</strong> {autopilot ? 'Autopilot' : 'Manual'}
                  </Typography>
                </Box>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSave}
                    disabled={!canSave || saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      backgroundColor: '#009688',
                      '&:hover': {
                        backgroundColor: '#00796b',
                      }
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Advanced Information Accordion */}
            <Box sx={{ mt: 4 }}>
              <Accordion elevation={1}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <InfoIcon sx={{ color: '#009688' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#009688' }}>
                      Advanced Information 
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#009688' }}>
                        URL Deletion and Sitemap Updates
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        When a page is deleted via data manager but then reappears in the sitemap, 
                        this could indicate important changes and is therefore re included in searcheble's data.
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          </CardContent>
        </Card>

        {/* Data Manager Component (shows when autopilot is off) */}
        {!autopilot && <DataManager />}

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSuccessMessage('')} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default CoreConfig;